import React, { useEffect, type ReactElement, type ReactNode } from 'react'
import ThemeInputDialog from 'common/ui/theme-input-dialog/ThemeInputDialog'
import { TextField, Typography } from '@mui/material'
import { object, string } from 'yup'
import { useFormik } from 'formik'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { assign } from 'lodash'
import { editVendor } from 'features/vendor/vendor.service'

import styles from './DescriptionDialog.module.scss'

interface DescriptionDialogProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const descriptionSchema = object({
  description: string().required('Ovo polje je obavezno').trim(),
})

const DescriptionDialog: React.FC<DescriptionDialogProps> = ({ isOpen, setIsOpen }): ReactElement => {
  const dispatch = useAppDispatch()
  const { validationErrors, vendor, editVendorStatus } = useAppSelector((state) => state.vendor)

  const formik = useFormik({
    validationSchema: descriptionSchema,
    initialValues: {
      description: vendor?.description,
    },
    onSubmit: async (values) => {
      await dispatch(editVendor(values))
      setIsOpen(false)
    },
  })

  useEffect(() => {
    if (validationErrors?.length) {
      formik.setErrors(assign({}, ...validationErrors.map((err) => ({ [err.type]: err.message }))))
    }
  }, [validationErrors])

  return (
    <ThemeInputDialog
      open={isOpen}
      title="Dodajte opis svoje kompanije"
      description="Ukoliko želite da admin ima neke dodatne informacije o Vama na profilu, u polju ispod možete napisati dodatne informacije/opis svoje kompanije."
      status={editVendorStatus}
      onClose={() => {
        setIsOpen(false)
      }}
      confirmButtonText="snimi"
      declineButtonText="izađi"
      confirmButtonAction={() => {
        formik.handleSubmit()
      }}
      confirmButtonDisabled={!formik.isValid || !formik.dirty}
      PaperProps={{
        sx: {
          width: '850px',
        },
      }}
    >
      <form className={styles.container}>
        <Typography variant="title3">Opis kompanije:</Typography>
        <TextField
          sx={{
            width: '100%',
          }}
          multiline
          rows={7}
          id="description"
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.description && !!formik.errors.description}
          helperText={formik.touched.description && (formik.errors.description as ReactNode)}
        />
      </form>
    </ThemeInputDialog>
  )
}

export default DescriptionDialog
